import { zodResolver } from "@hookform/resolvers/zod";
import { ActionFunctionArgs, LoaderFunctionArgs, json } from "@remix-run/node";
import { useFetcher, useNavigate } from "@remix-run/react";
import { Check, ChevronDown, Eye, EyeOff } from "lucide-react";
import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { sendOTPToMobile } from "~/.server/auth/otp";
import {
  AppBar,
  ButtonSubmit,
  Card,
  CardContent,
  Toolbar,
  toast,
  BottomNavBar,
  InputController,
  errorHandler,
  submitHandler,
  Clickable,
} from "@fr8/frui";
import { constants } from "~/utils/constants";
import { redirectIfAuthenticated } from '~/utils/auth/helper';
import shipperImage from "~/components/assets/shipper_img.png";
import carrierImage from "~/components/assets/carrier_img.png";
import { Errors } from "~/components/composed/indent/createIndent/indentChunks";
import CountryCodeSelect from "~/components/composed/common/countryCodeSelect";
import type { MetaFunction } from "@remix-run/node";

export const meta: MetaFunction = ({ location }) => {
  const searchParams = new URLSearchParams(location.search);
  const sourceName = searchParams.get("s_n");
  const destName = searchParams.get("d_n");

  if (sourceName && destName) {
    return [
      { title: `${sourceName} to ${destName} Truck Booking Sign Up | Open FR8` },
      {
        name: "description",
        content: `Create your Open FR8 account to book trucks from ${sourceName} to ${destName} — UAE's largest truck & load booking platform.`,
      },
      { name: "robots", content: "noindex, follow" },
    ];
  }

  return [
    { title: "Sign Up | Open FR8" },
    {
      name: "description",
      content: "Create your Open FR8 account — UAE's largest truck & load booking platform. Register and get trucks or loads within minutes.",
    },
  ];
};

export const links = () => [
  { rel: "canonical", href: "https://openfr8.com/uae/app/account/sign-up" },
];

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.json();
  const { phone_number, country_code } = formData;
  // mobileWithCode is country code without zero or plus sign ex: 971544454445
  const mobileWithCode = `${country_code}${phone_number}`;
  if (!phone_number) {
    return {
      status: false,
      description: "Invalid phone number kindly retry the process",
    };
  }
  return await sendOTPToMobile(mobileWithCode);
}

export async function loader({ request }: LoaderFunctionArgs) {
  const { getCommitHeaders } = await redirectIfAuthenticated(request);
  return json({}, { headers: await getCommitHeaders() });
}

function getUtmSource() {
  const urlSource = localStorage.getItem("utm_source");
  const playStoreSource = localStorage.getItem("campaign_utm_source");
  return playStoreSource || urlSource;
}

const createAccountFormSchema = z
  .object({
    phone_number: z.coerce
      .number({
        required_error: "Mobile number is required",
        invalid_type_error: "Invalid mobile number",
      })
      .refine((val) => /^[1-9]\d{6,9}$/.test(val.toString()), {
        message: "Enter valid mobile number",
      }),
    company_name: z.string().optional(),
    license_number: z.coerce
    .string().optional()
    .refine((val) => !val || /^[A-Za-z0-9]{4,}$/.test(val), {
      message: "License number must contain at least 4 numbers",
    })
    .refine((val) => !val || val.length <= 15, {
      message: "License number should not exceed 15 numbers",
    }),
    password: z.coerce
      .number({
        message: "Only numbers are allowed",
      })
      .refine((val) => val.toString().length >= 4, {
        message: "Pin must contain 4 digits",
      }),
    re_enter_password: z.coerce
      .number({
        message: "Only numbers are allowed",
      })
      .refine((val) => val.toString().length >= 4, {
        message: "Pin must contain 4 digits",
      }),
    user_type: z.string(),
    utm_source: z.string().nullable(),
    country_id: z
      .number({
        message: "Country code is required",
      })
      .default(1),
    country_code: z
      .string({
        message: "Country code is required",
      })
      .default("971"),
  })
  .refine((data) => data.password === data.re_enter_password, {
    message: "Pin do not match",
    path: ["re_enter_password"],
  });

const createAccountFormDefaults = {
  phone_number: undefined,
  company_name: "Unknown",
  license_number: undefined,
  password: undefined,
  re_enter_password: undefined,
  email: "",
  user_type: "",
  utm_source: undefined,
  country_id: 1,
  country_code: "971",
};

const CreateAccount = () => {
  const fetcher = useFetcher();
  const navigate = useNavigate();
  const isSubmitting = fetcher.state === "submitting";
  const [showCountrySelectModal, setShowCountrySelectModal] =
    useState<boolean>();

  const form = useForm<z.infer<typeof createAccountFormSchema>>({
    defaultValues: createAccountFormDefaults,
    reValidateMode: "onChange",
    mode: "onChange",
    resolver: zodResolver(createAccountFormSchema),
  });

  type ValidationSchema = z.infer<typeof createAccountFormSchema>;
  const onSubmit = (values: ValidationSchema) => {
    fetcher.submit(values, {
      method: "POST",
      encType: "application/json",
    });
  };

  const handleFormSubmit = submitHandler(form.handleSubmit, onSubmit, errorHandler());
  React.useEffect(() => {
    if (fetcher.data) {
      const data = fetcher.data as {status:boolean, result:{message: string}};
      if (data?.status) {
        navigate(constants.registrationOtp, {
          replace: true,
          state: {
            data: data,
            companyData: { ...form.getValues() },
          },
        });
      } else {
        toast.error("Error Occurred!", {
          description: `${data?.result?.message || ""}`,
        });
      }
    }
  }, [fetcher.data, navigate]);

  React.useEffect(() => {
    form.setValue("utm_source", getUtmSource());
  }, []);

  const accountTypeItems: Array<{
    id: number;
    header: string;
    type: "carrier" | "shipper";
    description: string;
    tabIndex: number;
    icon: string;
  }> = [
    {
      id: 1,
      header: "Carrier / Transporter",
      description: "I'm looking for loads",
      type: "carrier",
      tabIndex: 1,
      icon: carrierImage,
    },
    {
      id: 2,
      header: "Shipper / Fright forwarders",
      description: "I'm looking for trucks",
      type: "shipper",
      tabIndex: 2,
      icon: shipperImage,
    },
  ];

  const onClickUserType = (type: "carrier" | "shipper") => {
    form.setValue("user_type", type);
  };
  const userTypeWatcher = form.watch("user_type");
  const toggleCountryCode = () => setShowCountrySelectModal((prev) => !prev);
  const countryCodeWatcher = form.watch("country_code");

  return (
    <div className="pt-16 pb-16 w-full">
      <AppBar>
        <Toolbar showBackButton onBackClick={() => navigate(-1)} />
      </AppBar>
      <div className="mx-auto max-w-6xl px-2">
        <Card>
          <CardContent className="p-4">
        <h1 className="font-semibold text-2xl text-start">Sign up</h1>
        <p className="text-xl font-medium mb-4">Enter your details</p>
          <form
            onSubmit={handleFormSubmit}
            method="post"
            autoComplete="off"
            className="flex flex-col `justify-between gap-2"
          >
            <div>
              <Controller
                control={form.control}
                name={"phone_number"}
                render={({ field, fieldState }) => (
                  <InputController
                    field={{ ...field, value: field.value ?? "" }}
                    fieldState={fieldState}
                    label="Mobile Number"
                    placeholder="Enter your mobile"
                    type="tel"
                    inputMode="numeric"
                    required
                    maxLength={10}
                    minLength={6}
                    className="ml-4"
                    prefixIcon={
                      <Clickable
                        onClick={toggleCountryCode}
                        className="ml-4 flex flex-row items-center text-neutral-500 hover:font-bold"
                      >
                        <p className="text-xs">+{countryCodeWatcher}</p>
                        <ChevronDown size={16} />
                      </Clickable>
                    }
                    autoComplete="mobile tel"
                  />
                )}
              />
              <Errors error={form.formState.errors.country_code?.message} />
            </div>
          <Controller
            control={form.control}
            name={"company_name"}
            render={({ field, fieldState }) => (
              <InputController
                field={{ ...field, value: field.value ?? "" }}
                fieldState={fieldState}
                label="Company Name"
                placeholder="Enter company name"
                hidden
                //   required
                //   handleChange={(value: string) =>
                //     form.setValue("company_name", value?.toUpperCase())
                //   }
              />
            )}
            />
          <Controller
            control={form.control}
            name={"license_number"}
            render={({ field, fieldState }) => (
              <InputController
                field={{ ...field, value: field.value ?? "" }}
                fieldState={fieldState}
                label="License Number"
                placeholder="Enter License Number"
                hidden
                //   autoComplete="off"
                //   required
              />
            )}
          />
          <Controller
            control={form.control}
            name={"password"}
            render={({ field, fieldState }) => (
              <InputController
                field={{ ...field, value: field.value ?? "" }}
                fieldState={fieldState}
                label="Create pin"
                autoComplete="off"
                placeholder="Create 4 digit pin"
                maxLength={4}
                suffixIcon={<Eye size={14} className="text-gray-500" />}
                required
                type="tel"
                inputMode="numeric"
              />
            )}
          />
          <Controller
            control={form.control}
            name={"re_enter_password"}
            render={({ field, fieldState }) => (
              <InputController
                field={{ ...field, value: field.value ?? "" }}
                fieldState={fieldState}
                label="Re enter pin"
                placeholder="Re enter pin"
                required
                suffixIcon={<EyeOff size={14} className="text-gray-500" />}
                maxLength={4}
                type="password"
                autoComplete="off"
                inputMode="numeric"
              />
            )}
          />
          <h4 className="mt-2">
            Service Type <span className="text-destructive text-lg">*</span>{" "}
          </h4>
            <div className="gap-2 flex flex-col">
              {accountTypeItems.map((type) => (
                <Card
                  onClick={() => onClickUserType(type.type)}
                  tabIndex={type.tabIndex}
                  key={type.id}
                  className={`bg-accent/50 border h-fit cursor-pointer ${type.type === userTypeWatcher ? "border-secondary" : ""}`}
                >
                  <CardContent className="px-4 py-2">
                    <div className="flex items-center w-full justify-between">
                     <div className="flex items-center gap-4 py-1">
                        <picture className="h-14 w-14 shrink-0 overflow-hidden rounded-full border border-slate-300">
                          <img
                            src={type.icon}
                            width={512}
                            height={512}
                            className="h-full w-full"
                            alt="Company type"
                          />
                        </picture>
                        <div className="flex flex-col gap-1">
                        <h3 className="text-start font-semibold">
                            {type.header}
                        </h3>
                          <p className="text-start text-muted-foreground">
                            {type.description}
                          </p>
                        </div>
                      </div>
                      {type.type === userTypeWatcher ? (
                      <Check className="text-secondary" />
                      ) : null}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            <input type="hidden" name="utm_source" />
            <BottomNavBar>
              <div className="mx-auto w-full max-w-6xl">
                <ButtonSubmit
                  isLoading={isSubmitting}
                  disabled={!form.getValues("user_type")}
                  className="w-full"
                >
                  Continue
                </ButtonSubmit>
              </div>
            </BottomNavBar>
            {showCountrySelectModal ? (
              <CountryCodeSelect
                onSelectCountryCode={(value) => {
                  form.setValue("country_code", value.country_code);
                  form.setValue("country_id", value.country_id);
                  form.clearErrors("country_code");
                  form.clearErrors("country_id");
                }}
                onClose={toggleCountryCode}
                selectedValue={form.getValues("country_code")}
                open={showCountrySelectModal}
              />
            ) : null}
          </form>
        </CardContent></Card>
      </div>
    </div>
  );
};

export default CreateAccount;
